import { Fragment, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  Spinner,
  Stack,
  Table,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { LuChevronDown, LuChevronRight, LuPencil, LuPlus, LuSearch } from 'react-icons/lu';
import { listClients, type ClientContractSummary, type ClientListRow, type ClientStatus } from '@/lib/api';
import { ContractSummaryPanel } from '@/components/contract/ContractSummaryPanel';
import { useProject } from '@/lib/ProjectContext';

const DOC_LABEL: Record<ClientListRow['documentType'], string> = {
  dpi: 'DPI',
  passport: 'Pasaporte',
  nit: 'NIT',
  other: 'Otro',
};

export function Clients() {
  const navigate = useNavigate();
  const { activeProject } = useProject();
  const [tab, setTab] = useState<ClientStatus>('active');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [rows, setRows] = useState<ClientListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [contractCache, setContractCache] = useState<Record<string, ClientContractSummary[]>>({});

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setExpanded(new Set());
    setContractCache({});
  }, [activeProject?.id]);

  useEffect(() => {
    if (!activeProject) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listClients({
      projectId: activeProject.id,
      status: tab,
      q: debouncedQ || undefined,
    })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'No se pudo cargar la lista');
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProject?.id, tab, debouncedQ]);

  if (!activeProject) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Box
          bg="bg.surface"
          borderWidth="1px"
          borderColor="border"
          rounded="lg"
          p="10"
          textAlign="center"
          maxW="2xl"
          mx="auto"
        >
          <Text color="fg.muted">
            Selecciona un proyecto en la barra superior para ver sus clientes.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="6xl">
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'flex-end' }}
          gap="4"
        >
          <Stack gap="1">
            <Text
              fontSize="xs"
              color="fg.muted"
              textTransform="uppercase"
              letterSpacing="0.06em"
              fontWeight="700"
            >
              {activeProject.code} · {activeProject.name}
            </Text>
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              Clientes
            </Heading>
            <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
              Perfil único por persona con código auto-generado por proyecto.
            </Text>
          </Stack>
          <Button
            colorPalette="brand"
            bg="brand.600"
            color="white"
            _hover={{ bg: 'brand.700' }}
            onClick={() => navigate('/clientes/nuevo')}
            alignSelf={{ base: 'stretch', md: 'flex-end' }}
          >
            <Icon as={LuPlus} /> Nuevo cliente
          </Button>
        </Stack>

        <Tabs.Root value={tab} onValueChange={(v) => setTab(v.value as ClientStatus)}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            gap="3"
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
          >
            <Tabs.List>
              <Tabs.Trigger value="active">Activos</Tabs.Trigger>
              <Tabs.Trigger value="archived">Archivados</Tabs.Trigger>
            </Tabs.List>
            <InputGroup
              startElement={<Icon as={LuSearch} color="fg.muted" />}
              w={{ base: 'full', md: '320px' }}
            >
              <Input
                value={q}
                onChange={(e) => setQ(e.currentTarget.value)}
                placeholder="Buscar por nombre, DPI, código…"
              />
            </InputGroup>
          </Stack>

          <Box pt="6">
            {error && (
              <Text color="red.600" fontSize="sm" mb="4">
                {error}
              </Text>
            )}
            {loading ? (
              <HStack gap="3" color="fg.muted">
                <Spinner size="sm" />
                <Text fontSize="sm">Cargando…</Text>
              </HStack>
            ) : rows.length === 0 ? (
              <Box
                bg="bg.surface"
                borderWidth="1px"
                borderColor="border"
                rounded="lg"
                p="10"
                textAlign="center"
              >
                <Text color="fg.muted" fontSize="sm">
                  {debouncedQ
                    ? `No hay clientes que coincidan con "${debouncedQ}".`
                    : tab === 'active'
                      ? 'Aún no hay clientes en este proyecto. Crea el primero para empezar.'
                      : 'No hay clientes archivados en este proyecto.'}
                </Text>
              </Box>
            ) : (
              <>
                {/* Mobile: card list */}
                <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
                  {rows.map((c) => (
                    <Box
                      key={c.membershipId}
                      bg="bg.surface"
                      borderWidth="1px"
                      borderColor="border"
                      rounded="lg"
                      p="4"
                    >
                      <Stack gap="3">
                        <Stack gap="1">
                          <HStack justify="space-between" align="flex-start" gap="3">
                            <Stack gap="0.5" minW={0}>
                              <Text fontSize="xs" color="fg.muted" fontWeight="700">
                                {c.code}
                              </Text>
                              <Text fontWeight="600" lineHeight="1.2">
                                {c.fullName}
                              </Text>
                            </Stack>
                            <Badge
                              colorPalette={c.status === 'active' ? 'green' : 'gray'}
                              flexShrink={0}
                            >
                              {c.status === 'active' ? 'Activo' : 'Archivado'}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="fg.muted">
                            {DOC_LABEL[c.documentType]} · {c.documentNumber}
                          </Text>
                          {(c.email || c.phone) && (
                            <Text fontSize="sm" color="fg.muted" truncate>
                              {[c.phone, c.email].filter(Boolean).join(' · ')}
                            </Text>
                          )}
                        </Stack>
                        <HStack gap="2" justify="space-between" wrap="wrap">
                          <ContractCountChip count={c.contractCount} />
                          <HStack gap="2">
                            <Button
                              size="xs"
                              variant="ghost"
                              aria-label={expanded.has(c.id) ? 'Ocultar contratos' : 'Mostrar contratos'}
                              onClick={() => toggleExpand(c.id)}
                            >
                              <Icon as={expanded.has(c.id) ? LuChevronDown : LuChevronRight} />
                              Contrato
                            </Button>
                            <Button size="xs" variant="outline" asChild>
                              <RouterLink to={`/clientes/${c.id}`}>
                                <Icon as={LuPencil} /> Editar
                              </RouterLink>
                            </Button>
                          </HStack>
                        </HStack>
                        {expanded.has(c.id) && activeProject && (
                          <Box borderTopWidth="1px" borderColor="border" pt="3">
                            <ContractSummaryPanel
                              clientId={c.id}
                              projectId={activeProject.id}
                              cached={contractCache[c.id]}
                              onLoaded={(d) => setContractCache((p) => ({ ...p, [c.id]: d }))}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                {/* Desktop: table */}
                <Box
                  bg="bg.surface"
                  borderWidth="1px"
                  borderColor="border"
                  rounded="lg"
                  overflow="hidden"
                  display={{ base: 'none', md: 'block' }}
                >
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Contrato</Table.ColumnHeader>
                        <Table.ColumnHeader>Código</Table.ColumnHeader>
                        <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                        <Table.ColumnHeader>Documento</Table.ColumnHeader>
                        <Table.ColumnHeader>Contacto</Table.ColumnHeader>
                        <Table.ColumnHeader>Estado</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Acciones</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rows.map((c) => (
                        <Fragment key={c.membershipId}>
                          <Table.Row>
                            <Table.Cell>
                              <HStack gap="2">
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  aria-label={expanded.has(c.id) ? 'Ocultar contratos' : 'Mostrar contratos'}
                                  onClick={() => toggleExpand(c.id)}
                                >
                                  <Icon as={expanded.has(c.id) ? LuChevronDown : LuChevronRight} />
                                </Button>
                                <ContractCountChip count={c.contractCount} />
                              </HStack>
                            </Table.Cell>
                            <Table.Cell fontWeight="600">{c.code}</Table.Cell>
                            <Table.Cell>{c.fullName}</Table.Cell>
                            <Table.Cell color="fg.muted">
                              {DOC_LABEL[c.documentType]} · {c.documentNumber}
                            </Table.Cell>
                            <Table.Cell color="fg.muted">
                              {[c.phone, c.email].filter(Boolean).join(' · ') || '—'}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge colorPalette={c.status === 'active' ? 'green' : 'gray'}>
                                {c.status === 'active' ? 'Activo' : 'Archivado'}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              <HStack justify="flex-end" gap="2">
                                <Button size="xs" variant="outline" asChild>
                                  <RouterLink to={`/clientes/${c.id}`}>
                                    <Icon as={LuPencil} /> Editar
                                  </RouterLink>
                                </Button>
                              </HStack>
                            </Table.Cell>
                          </Table.Row>
                          {expanded.has(c.id) && activeProject && (
                            <Table.Row>
                              <Table.Cell colSpan={7} bg="bg.subtle">
                                <ContractSummaryPanel
                                  clientId={c.id}
                                  projectId={activeProject.id}
                                  cached={contractCache[c.id]}
                                  onLoaded={(d) => setContractCache((p) => ({ ...p, [c.id]: d }))}
                                />
                              </Table.Cell>
                            </Table.Row>
                          )}
                        </Fragment>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </>
            )}
          </Box>
        </Tabs.Root>
      </Stack>
    </Box>
  );
}

function ContractCountChip({ count }: { count: number }) {
  if (count === 0) {
    return (
      <Badge colorPalette="gray" size="sm">
        Sin contrato
      </Badge>
    );
  }
  return (
    <Badge colorPalette="blue" size="sm">
      {count === 1 ? '1 contrato' : `${count} contratos`}
    </Badge>
  );
}
